import { TOKENS } from "~/constants";
import { useDagProgram } from "./useDagProgram";
import { BN } from "@coral-xyz/anchor";
import { useConnection } from "@solana/wallet-adapter-react";
import { lamportsToSol } from "~/utils/conversion";
import useSWR from "swr";
import { useAccount } from "./useAccount";

export const useVotingPower = ({ token, uiAmount }: { token: typeof TOKENS[keyof typeof TOKENS], uiAmount: BN }) => {
  const { program, getUserLockPda, getProjectPda } = useDagProgram();
  const { account } = useAccount()

  const fetch = async () => {
    if (!program || !account.address) return { currentVotingPower: 0, totalVotingPower: 0 };

    const projectPda = getProjectPda(token.address);
    const userLockPda = getUserLockPda(account.address, projectPda);
    const currentTime = new BN(Math.round(Date.now() / 1000));

    try {
      // Fetch current voting power
      const currentVotingPower = await program.methods
        .calculateUserVotingPower(currentTime)
        .accountsStrict({ userLock: userLockPda })
        .view()
        .catch(() => new BN(0));

      // Total voting power is the same as current if no new amount
      let totalVotingPower = currentVotingPower;

      // If there's a new amount being staked, we need to add its voting power
      if (!uiAmount.isZero()) {
        // We'll use the same calculation since the program will handle the
        // voting power calculation based on the lock amount and duration
        totalVotingPower = currentVotingPower.add(uiAmount);
      }
      
      console.log('Current voting power:', currentVotingPower.toString());
      console.log('Total voting power:', totalVotingPower.toString());

      return {
        currentVotingPower: currentVotingPower.toNumber(),
        totalVotingPower: totalVotingPower.toNumber(),
      };
    } catch (error) {
      console.error('Error fetching voting power:', error);
      return { currentVotingPower: 0, totalVotingPower: 0 };
    }
  }

  const { data, isLoading, mutate } = useSWR(
    program && account.address ? ['votingPower', token.address.toString(), account.address.toString(), uiAmount.toString()] : null,
    fetch,
    {
      refreshInterval: 1000 * 10,
    }
  );
  
  return {
    currentVotingPower: data?.currentVotingPower ?? 0,
    totalVotingPower: data?.totalVotingPower ?? 0,
    isLoading,
    mutate
  }
}